import { useEffect, useState } from 'react';
import { getJuiz, getJuizPiso } from '@/lib/api';
import { useDateRangeKey } from '@/lib/dateRange';

interface PisoUlt { processo_id: number; data: string; status: string; score: number | null; piso: number | null; aplicado: boolean; kb_shadow: number | null; motivo: string }
interface PisoData {
  auto_apply_rate: number | null; n_piso_deferido: number;
  distribuicao_status: Record<string, number>; aplicados: number;
  pct_categoria_diferenciada: number | null; pct_sem_cct_nos_autos: number | null;
  kb_shadow_hits: number; kb: { fonte: string; n: number; validados: number }[]; ultimos: PisoUlt[];
}

const STATUS_COLORS: Record<string, string> = {
  AUTO_APPLY: 'text-emerald-500', SUGGEST_REVIEW: 'text-amber-400',
  REQUIRES_HUMAN_INPUT: 'text-orange-400', DO_NOT_APPLY: 'text-slate-500',
};

function PisoSection() {
  const rangeKey = useDateRangeKey();
  const [p, setP] = useState<PisoData | null>(null);
  useEffect(() => {
    let alive = true;
    getJuizPiso().then((r) => { if (alive) setP(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [rangeKey]);
  if (!p) return null;
  return (
    <div className="rounded-xl bg-slate-800 p-4">
      <div className="text-sm font-semibold text-slate-200 mb-3">Piso CCT — resolver (4 status do perito)</div>
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div><div className="text-xs text-slate-400">AUTO_APPLY_RATE (piso deferido)</div>
          <div className="text-2xl font-bold text-teal-400">{p.auto_apply_rate != null ? `${p.auto_apply_rate}%` : '—'}</div>
          <div className="text-xs text-slate-500">{p.n_piso_deferido} caso(s) c/ piso deferido</div></div>
        <div><div className="text-xs text-slate-400">Aplicados (verba criada)</div>
          <div className="text-2xl font-bold text-emerald-500">{p.aplicados}</div></div>
        <div><div className="text-xs text-slate-400">% sem CCT nos autos</div>
          <div className="text-2xl font-bold text-slate-200">{p.pct_sem_cct_nos_autos != null ? `${p.pct_sem_cct_nos_autos}%` : '—'}</div>
          <div className="text-xs text-slate-500">KB shadow hits: {p.kb_shadow_hits}</div></div>
        <div><div className="text-xs text-slate-400">Base de pisos (KB)</div>
          <div className="text-sm text-slate-200">{p.kb.map((k) => `${k.fonte}: ${k.n}`).join(' · ') || 'vazia'}</div></div>
      </div>
      <div className="flex gap-4 mb-3 text-xs">
        {Object.entries(p.distribuicao_status).map(([s, n]) => (
          <span key={s} className={STATUS_COLORS[s] || 'text-slate-400'}>{s}: <b>{n}</b></span>
        ))}
      </div>
      {p.ultimos.length > 0 && (
        <table className="w-full text-xs">
          <thead><tr className="text-slate-500"><th className="text-left">Caso</th><th className="text-left">Status</th><th className="text-right">Score</th><th className="text-right">Piso</th><th className="text-left pl-3">Motivo</th></tr></thead>
          <tbody>
            {p.ultimos.slice(0, 12).map((u, i) => (
              <tr key={i} className="border-t border-slate-700/40">
                <td className="py-1 text-slate-300">#{u.processo_id}</td>
                <td className={STATUS_COLORS[u.status] || 'text-slate-400'}>{u.status}{u.aplicado ? ' ✓' : ''}</td>
                <td className="text-right text-slate-300">{u.score ?? ''}</td>
                <td className="text-right text-slate-300">{u.piso != null ? u.piso.toLocaleString('pt-BR') : (u.kb_shadow != null ? `KB:${u.kb_shadow.toLocaleString('pt-BR')}` : '')}</td>
                <td className="pl-3 text-slate-500">{u.motivo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface SeriePt { periodo: string; score: number; n: number }
interface Caso { processo_id: number; data: string; score: number; defeitos: number }
interface Verba { verba: string; defeitos: number; impacto: number; altas: number; score: number }
interface Resumo {
  score_medio: number | null; n_casos: number; n_limpos: number; n_criticos: number; org_id: number | null;
}
interface JuizData { resumo: Resumo; serie: SeriePt[]; casos: Caso[]; verbas: Verba[] }

const cor = (s: number) => (s >= 90 ? 'text-emerald-600' : s >= 60 ? 'text-amber-500' : 'text-red-600');
const bg = (s: number) => (s >= 90 ? 'bg-emerald-500' : s >= 60 ? 'bg-amber-400' : 'bg-red-500');

// chart SVG inline (score agregado por semana) — sem dependência
function ScoreChart({ data }: { data: SeriePt[] }) {
  if (!data || data.length < 2) {
    return <div className="text-xs text-slate-400">Poucos períodos para o gráfico (mín. 2 dias com casos avaliados).</div>;
  }
  const W = 760, H = 200, padL = 34, padR = 14, padT = 14, padB = 30;
  const ys = data.map((d) => d.score);
  const minY = Math.max(0, Math.min(...ys) - 8);
  const maxY = Math.min(100, Math.max(...ys) + 8);
  const sx = (i: number) => padL + (i / Math.max(1, data.length - 1)) * (W - padL - padR);
  const sy = (v: number) => padT + (1 - (v - minY) / Math.max(1, maxY - minY)) * (H - padT - padB);
  const path = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${sx(i).toFixed(1)} ${sy(d.score).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      {[0, 25, 50, 75, 100].map((g) => (
        <g key={g}>
          <line x1={padL} y1={sy(g)} x2={W - padR} y2={sy(g)} stroke="#e2e8f0" strokeWidth={1} />
          <text x={4} y={sy(g) + 3} fontSize={9} fill="#94a3b8">{g}</text>
        </g>
      ))}
      <path d={path} fill="none" stroke="#14b8a6" strokeWidth={2.5} />
      {data.map((d, i) => (
        <g key={d.periodo}>
          <circle cx={sx(i)} cy={sy(d.score)} r={3.5} fill="#0d9488">
            <title>{`${d.periodo}: ${d.score} (n=${d.n})`}</title>
          </circle>
          {(i === 0 || i === data.length - 1 || i % Math.ceil(data.length / 8) === 0) && (
            <text x={sx(i)} y={H - 8} fontSize={8} fill="#94a3b8" textAnchor="middle">{d.periodo.slice(5)}</text>
          )}
        </g>
      ))}
    </svg>
  );
}

export function JuizPage() {
  const rangeKey = useDateRangeKey();
  const [org, setOrg] = useState<string>('');
  const [data, setData] = useState<JuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    const params: any = {};
    if (org.trim()) params.org_id = Number(org.trim());
    getJuiz(params)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || e.message || 'erro'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [rangeKey, org]);

  const R = data?.resumo;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Juiz — Score de Qualidade</h1>
          <p className="text-sm text-slate-400">Score 0-100 ponderado pelo 2º juiz adversarial: defeito confirmado = peso cheio · não-verificado = meio peso · refutado = zero. Por caso, por verba e evolução.</p>
        </div>
        <input
          value={org} onChange={(e) => setOrg(e.target.value)}
          placeholder="filtrar por org (ex: 1094 = Hugo)"
          className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm w-64"
        />
      </div>

      {loading && <div className="text-slate-400">Carregando…</div>}
      {err && <div className="text-red-400 text-sm">Erro: {err}</div>}

      {R && (
        <>
          <div className="grid grid-cols-4 gap-4">
            <div className="rounded-xl bg-slate-800 p-4">
              <div className="text-xs text-slate-400">Score médio do período</div>
              <div className={`text-3xl font-bold ${R.score_medio != null ? cor(R.score_medio) : 'text-slate-500'}`}>
                {R.score_medio != null ? R.score_medio : '—'}<span className="text-base text-slate-500">/100</span>
              </div>
            </div>
            <div className="rounded-xl bg-slate-800 p-4"><div className="text-xs text-slate-400">Casos avaliados</div><div className="text-3xl font-bold text-slate-100">{R.n_casos}</div></div>
            <div className="rounded-xl bg-slate-800 p-4"><div className="text-xs text-slate-400">Limpos (≥90)</div><div className="text-3xl font-bold text-emerald-500">{R.n_limpos}</div></div>
            <div className="rounded-xl bg-slate-800 p-4"><div className="text-xs text-slate-400">Críticos (&lt;60)</div><div className="text-3xl font-bold text-red-500">{R.n_criticos}</div></div>
          </div>

          <div className="rounded-xl bg-slate-800 p-4">
            <div className="text-sm font-semibold text-slate-200 mb-2">Evolução do score (por dia)</div>
            <ScoreChart data={data!.serie} />
          </div>

          <PisoSection />

          <div className="grid grid-cols-2 gap-6">
            <div className="rounded-xl bg-slate-800 p-4">
              <div className="text-sm font-semibold text-slate-200 mb-3">Score por verba (as que mais pesam)</div>
              <table className="w-full text-sm">
                <thead><tr className="text-slate-400 text-xs"><th className="text-left">Verba</th><th className="text-right">Defeitos</th><th className="text-right">Altas</th><th className="text-right">Score</th></tr></thead>
                <tbody>
                  {data!.verbas.map((v) => (
                    <tr key={v.verba} className="border-t border-slate-700/50">
                      <td className="py-1.5 text-slate-200">{v.verba}</td>
                      <td className="text-right text-slate-300">{v.defeitos}</td>
                      <td className="text-right text-red-400">{v.altas || ''}</td>
                      <td className={`text-right font-semibold ${cor(v.score)}`}>{v.score}</td>
                    </tr>
                  ))}
                  {data!.verbas.length === 0 && <tr><td colSpan={4} className="py-3 text-slate-500 text-center">Sem defeitos no período 🎉</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="rounded-xl bg-slate-800 p-4">
              <div className="text-sm font-semibold text-slate-200 mb-3">Casos (mais recentes)</div>
              <table className="w-full text-sm">
                <thead><tr className="text-slate-400 text-xs"><th className="text-left">Caso</th><th className="text-left">Data</th><th className="text-right">Defeitos</th><th className="text-right">Score</th></tr></thead>
                <tbody>
                  {data!.casos.map((c) => (
                    <tr key={c.processo_id} className="border-t border-slate-700/50">
                      <td className="py-1.5 text-slate-200">#{c.processo_id}</td>
                      <td className="text-slate-400">{c.data}</td>
                      <td className="text-right text-slate-300">{c.defeitos}</td>
                      <td className="text-right"><span className={`inline-block w-9 text-center rounded text-white text-xs py-0.5 ${bg(c.score)}`}>{c.score}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
